/**
 * The background uploader — chunked, resumable, strictly ordered, fail-loud (FSD §4.5).
 *
 * Invariants:
 *   • ONE in-flight request at a time; chunks go up in (shot ordinal, seq) order, and chunk i+1 is
 *     never sent before chunk i returned 204 — the server recomputes h_i from h_{i-1} at first
 *     touch, so order is part of the protocol, not an optimisation.
 *   • Network / 5xx → exponential backoff, unbounded while the queue has work (store-and-forward:
 *     a representative on a 2G roof keeps recording; the queue drains when the link returns). A 5xx
 *     that says when to come back (`Retry-After`) is never re-sent sooner; `503 SV_REDACTION_UNAVAILABLE`
 *     (contract §14f: the ID photo could not be masked yet, nothing stored) waits at least its documented
 *     30 s even where the page cannot read the header — each re-send is the whole photo and a redactor call.
 *   • 422 on an ID-document photo (`SV_DOCUMENT_NOT_RECOGNISED`, `SV_IMAGE_TOO_LARGE`, `SV_IMAGE_UNDECODABLE`,
 *     `SV_DOCUMENT_NOT_ONE_CHUNK`) → refused like any 4xx: the customer takes a NEW photo, these bytes are
 *     never sent again.
 *   • Offline (`navigator.onLine === false`) → wait for `online`, then continue where it stopped.
 *   • 422 SV_CHAIN_MISMATCH → the SHOT is refused (not retried — the server's recompute disagreed,
 *     fail-closed) and the UI is told so it can re-record that shot honestly.
 *   • 409 SV_SHOT_SEALED → the server already holds this shot (a lost 204 on complete): treat as
 *     complete, never as an error to loop on.
 *   • 401/403 → the bearer died; pause and ask the app to re-exchange (`auth-required`).
 *   • Any other 4xx → refuse the shot with that code. A 4xx is an answer, not a transient.
 *   • A shot's chunks are HELD (persisted, not sent) until the shot is sealed locally — i.e. its edge
 *     quality gate passed. Reason: the be treats a repeated seq 0 on a non-refused shot as an
 *     idempotent resume (new bytes dropped), so streaming mid-recording and then retaking after a
 *     failed gate would surface as a spurious SV_CHAIN_MISMATCH + an audit refusal. Uploads still run
 *     in the background — they overlap the NEXT shot's capture, and never block the UI.
 *   • Never blocks the capture UI: everything here is async and event-driven.
 */

import type { ChunkHeaders, CompleteShotBody, CompleteShotResponse } from '@/api/capture';
import { SvApiError, SvNetworkError } from '@/api/errors';
import { DEFAULT_BACKOFF, nextDelay, type BackoffPolicy } from './backoff';
import type { QueueStorage, StoredChunk, StoredShot } from './chunkQueue';

/** Contract §14f: `503 SV_REDACTION_UNAVAILABLE` carries `Retry-After: 30` — the floor when the header is not readable. */
export const REDACTION_RETRY_AFTER_S = 30;

export type UploaderEvent =
  | { readonly type: 'chunk-uploaded'; readonly shotId: string; readonly seq: number }
  | { readonly type: 'shot-complete'; readonly shotId: string; readonly serverSha256: string | null }
  | { readonly type: 'shot-refused'; readonly shotId: string; readonly code: string; readonly message: string }
  | { readonly type: 'offline' }
  | { readonly type: 'online' }
  | { readonly type: 'retrying'; readonly delayMs: number; readonly attempt: number; readonly shotId: string; readonly seq: number | null }
  | { readonly type: 'auth-required' }
  | { readonly type: 'idle' };

export interface UploaderDeps {
  readonly storage: QueueStorage;
  readonly putChunk: (shotId: string, seq: number, bytes: Uint8Array, headers: ChunkHeaders) => Promise<void>;
  readonly completeShot: (shotId: string, body: CompleteShotBody) => Promise<CompleteShotResponse>;
  readonly isOnline?: () => boolean;
  readonly backoff?: BackoffPolicy;
  readonly random?: () => number;
  /** Injectable clock for the backoff wait (tests use fake timers through it). */
  readonly setTimer?: (fn: () => void, ms: number) => unknown;
  readonly clearTimer?: (handle: unknown) => void;
}

export interface UploaderSnapshot {
  readonly running: boolean;
  readonly pendingChunks: number;
  readonly inFlight: { readonly shotId: string; readonly seq: number | null } | null;
  readonly refusedShots: readonly string[];
  readonly consecutiveFailures: number;
  readonly waitingForAuth: boolean;
}

type Next =
  | { readonly kind: 'chunk'; readonly chunk: StoredChunk; readonly shot: StoredShot }
  | { readonly kind: 'complete'; readonly shot: StoredShot }
  | null;

export class Uploader {
  private running = false;
  private visitId: string | null = null;
  private listeners = new Set<(e: UploaderEvent) => void>();
  private wake: (() => void) | null = null;
  private inFlight: { shotId: string; seq: number | null } | null = null;
  private consecutiveFailures = 0;
  private waitingForAuth = false;
  private refused = new Set<string>();
  /** The server's own wait before the next re-try (Retry-After), set by `classify`, taken once by `run`. */
  private retryFloorMs = 0;
  /** Until when that wait holds — a kick (new work, the review screen) never re-sends sooner than the server asked. */
  private notBefore = 0;
  private idleResolvers: Array<() => void> = [];
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;

  constructor(private readonly deps: UploaderDeps) {}

  on(listener: (e: UploaderEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(e: UploaderEvent): void {
    for (const l of this.listeners) {
      try {
        l(e);
      } catch {
        /* a listener fault never stops the queue */
      }
    }
  }

  snapshot(): UploaderSnapshot {
    return {
      running: this.running,
      pendingChunks: -1, // computed lazily by pending()
      inFlight: this.inFlight,
      refusedShots: [...this.refused],
      consecutiveFailures: this.consecutiveFailures,
      waitingForAuth: this.waitingForAuth,
    };
  }

  async pending(): Promise<number> {
    if (!this.visitId) return 0;
    const chunks = await this.deps.storage.listChunks(this.visitId);
    return chunks.filter((c) => c.status === 'pending').length;
  }

  start(visitId: string): void {
    if (this.running) return;
    this.visitId = visitId;
    this.running = true;
    if (typeof window !== 'undefined') {
      this.onlineHandler = () => {
        this.emit({ type: 'online' });
        this.kick();
      };
      this.offlineHandler = () => this.emit({ type: 'offline' });
      window.addEventListener('online', this.onlineHandler);
      window.addEventListener('offline', this.offlineHandler);
    }
    void this.run();
  }

  stop(): void {
    this.running = false;
    if (typeof window !== 'undefined') {
      if (this.onlineHandler) window.removeEventListener('online', this.onlineHandler);
      if (this.offlineHandler) window.removeEventListener('offline', this.offlineHandler);
    }
    this.onlineHandler = null;
    this.offlineHandler = null;
    this.kick();
  }

  /** Wake the loop: new work enqueued, network back, bearer refreshed. */
  kick(): void {
    this.waitingForAuth = false;
    const w = this.wake;
    this.wake = null;
    if (w) w();
  }

  /** Resolves the next time the queue has nothing to do (tests; the submit screen). */
  whenIdle(): Promise<void> {
    return new Promise((resolve) => {
      this.idleResolvers.push(resolve);
      this.kick();
    });
  }

  private async waitForKick(ms?: number): Promise<void> {
    await new Promise<void>((resolve) => {
      let handle: unknown = null;
      const set = this.deps.setTimer ?? ((fn: () => void, d: number) => setTimeout(fn, d));
      const clear = this.deps.clearTimer ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>));
      this.wake = () => {
        if (handle !== null) clear(handle);
        resolve();
      };
      if (ms !== undefined) handle = set(() => this.kick(), ms);
    });
  }

  private isOnline(): boolean {
    if (this.deps.isOnline) return this.deps.isOnline();
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  }

  private async pickNext(): Promise<Next> {
    const visitId = this.visitId as string;
    const shots = await this.deps.storage.listShots(visitId);
    for (const shot of shots) {
      if (shot.status !== 'sealed') continue; // recording → held; refused/abandoned/complete → nothing to do
      const chunks = await this.deps.storage.listChunks(visitId, shot.shotId, shot.attempt);
      const pending = chunks.find((c) => c.status === 'pending');
      if (pending) return { kind: 'chunk', chunk: pending, shot };
      const done = chunks.filter((c) => c.status === 'done').length;
      if (shot.status === 'sealed' && shot.chunkCount !== null && done === shot.chunkCount) {
        return { kind: 'complete', shot };
      }
    }
    return null;
  }

  private async run(): Promise<void> {
    while (this.running) {
      if (!this.isOnline()) {
        this.emit({ type: 'offline' });
        await this.waitForKick();
        continue;
      }
      if (this.waitingForAuth) {
        await this.waitForKick();
        continue;
      }
      const hold = this.notBefore - Date.now();
      if (hold > 0) {
        await this.waitForKick(hold);
        continue;
      }
      const next = await this.pickNext();
      if (!next) {
        this.inFlight = null;
        this.emit({ type: 'idle' });
        const rs = this.idleResolvers;
        this.idleResolvers = [];
        for (const r of rs) r();
        await this.waitForKick();
        continue;
      }
      const outcome = next.kind === 'chunk' ? await this.sendChunk(next.chunk, next.shot) : await this.sendComplete(next.shot);
      if (outcome === 'retry') {
        this.consecutiveFailures += 1;
        const delayMs = Math.max(nextDelay(this.consecutiveFailures, this.deps.backoff ?? DEFAULT_BACKOFF, this.deps.random), this.retryFloorMs);
        this.notBefore = this.retryFloorMs > 0 ? Date.now() + this.retryFloorMs : 0;
        this.retryFloorMs = 0;
        this.emit({
          type: 'retrying',
          delayMs,
          attempt: this.consecutiveFailures,
          shotId: next.kind === 'chunk' ? next.chunk.shotId : next.shot.shotId,
          seq: next.kind === 'chunk' ? next.chunk.seq : null,
        });
        await this.waitForKick(delayMs);
      } else if (outcome === 'auth') {
        this.waitingForAuth = true;
        this.emit({ type: 'auth-required' });
      } else {
        this.consecutiveFailures = 0;
      }
    }
  }

  private async refuseShot(shot: StoredShot, code: string, message: string): Promise<void> {
    this.refused.add(shot.shotId);
    await this.deps.storage.updateShot(shot.key, { status: 'refused', refusalCode: code, refusalMessage: message });
    // DEC-097 T2: a refused attempt is never sent again (a retake is a new attempt), so its bytes have no purpose on this
    // phone — and on an ID-document 422 they are a raw, UNMASKED photo of the customer's card the server refused to keep.
    // The shot row stays (status + code: the flow returns to it); only the bytes go. Best effort, like every purge.
    try {
      await this.deps.storage.deleteShotAttempt(shot.visitId, shot.shotId, shot.attempt);
    } catch {
      /* the boot sweep clears a refused visit's rows */
    }
    this.emit({ type: 'shot-refused', shotId: shot.shotId, code, message });
  }

  private async completeShotLocally(shot: StoredShot, serverSha256: string | null): Promise<void> {
    await this.deps.storage.updateShot(shot.key, { status: 'complete', serverSha256 });
    this.emit({ type: 'shot-complete', shotId: shot.shotId, serverSha256 });
  }

  private async sendChunk(chunk: StoredChunk, shot: StoredShot): Promise<'ok' | 'retry' | 'auth' | 'refused'> {
    this.inFlight = { shotId: chunk.shotId, seq: chunk.seq };
    try {
      await this.deps.putChunk(chunk.shotId, chunk.seq, new Uint8Array(chunk.bytes), {
        chainHash: chunk.chainHash,
        chunkSha256: chunk.sha256,
        capturedAt: chunk.headers.capturedAt,
        promptId: chunk.headers.promptId,
        geo: chunk.headers.geo,
        imu: chunk.headers.imu,
      });
      await this.deps.storage.updateChunk(chunk.key, { status: 'done', attempts: chunk.attempts + 1, lastError: null });
      this.emit({ type: 'chunk-uploaded', shotId: chunk.shotId, seq: chunk.seq });
      return 'ok';
    } catch (e) {
      return this.classify(e, chunk, shot);
    } finally {
      this.inFlight = null;
    }
  }

  private async sendComplete(shot: StoredShot): Promise<'ok' | 'retry' | 'auth' | 'refused'> {
    this.inFlight = { shotId: shot.shotId, seq: null };
    try {
      const body: CompleteShotBody = {
        chunkCount: shot.chunkCount as number,
        finalChainHash: shot.finalChainHash as string,
        mimeType: shot.mimeType,
        ...(shot.durationS !== null ? { durationS: shot.durationS } : {}),
        ...(shot.signature ? { signature: shot.signature } : {}),
      };
      const res = await this.deps.completeShot(shot.shotId, body);
      await this.completeShotLocally(shot, res.serverSha256);
      return 'ok';
    } catch (e) {
      if (e instanceof SvApiError && e.code === 'SV_CHUNKS_MISSING') {
        if (shot.completeAttempts === 0) {
          // The server lost bytes we believe we sent: re-send the whole attempt once, in order.
          const chunks = await this.deps.storage.listChunks(shot.visitId, shot.shotId, shot.attempt);
          for (const c of chunks) await this.deps.storage.updateChunk(c.key, { status: 'pending' });
          await this.deps.storage.updateShot(shot.key, { completeAttempts: 1 });
          return 'ok';
        }
        await this.refuseShot(shot, e.code, e.message);
        return 'refused';
      }
      return this.classify(e, null, shot);
    } finally {
      this.inFlight = null;
    }
  }

  private async classify(e: unknown, chunk: StoredChunk | null, shot: StoredShot): Promise<'retry' | 'auth' | 'refused' | 'ok'> {
    if (e instanceof SvNetworkError) {
      if (chunk) await this.deps.storage.updateChunk(chunk.key, { attempts: chunk.attempts + 1, lastError: e.message });
      return 'retry';
    }
    if (e instanceof SvApiError) {
      if (e.status >= 500) {
        if (chunk) await this.deps.storage.updateChunk(chunk.key, { attempts: chunk.attempts + 1, lastError: e.message });
        const afterS = e.retryAfterS ?? (e.code === 'SV_REDACTION_UNAVAILABLE' ? REDACTION_RETRY_AFTER_S : 0);
        this.retryFloorMs = afterS * 1000;
        return 'retry';
      }
      if (e.status === 401 || e.status === 403) return 'auth';
      if (chunk && e.code === 'SV_CHUNKS_MISSING' && shot.completeAttempts === 0) {
        // The server says an earlier chunk never landed (a lost write). Re-send the whole attempt once,
        // in order — accepted chunks are idempotent server-side, so this costs bandwidth, not correctness.
        const all = await this.deps.storage.listChunks(shot.visitId, shot.shotId, shot.attempt);
        for (const c of all) await this.deps.storage.updateChunk(c.key, { status: 'pending' });
        await this.deps.storage.updateShot(shot.key, { completeAttempts: 1 });
        return 'ok';
      }
      if (e.code === 'SV_SHOT_SEALED') {
        // Server truth wins: it already holds this shot. Mark everything done and move on.
        const chunks = await this.deps.storage.listChunks(shot.visitId, shot.shotId, shot.attempt);
        for (const c of chunks) if (c.status === 'pending') await this.deps.storage.updateChunk(c.key, { status: 'done' });
        await this.completeShotLocally(shot, null);
        return 'ok';
      }
      if (chunk) await this.deps.storage.updateChunk(chunk.key, { status: 'refused', lastError: e.message });
      await this.refuseShot(shot, e.code === 'UNKNOWN' ? `HTTP_${e.status}` : e.code, e.message);
      return 'refused';
    }
    // An unexpected throw (a bug) is surfaced as a refusal rather than an infinite retry.
    await this.refuseShot(shot, 'CLIENT_ERROR', e instanceof Error ? e.message : 'upload failed');
    return 'refused';
  }
}
